import React from 'react'
import styled from 'styled-components'
import { NavLink } from 'react-router-dom'
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa'
import { EXTERNAL_LINK } from '../assets/data/external_link'

const Footer = () => {
  return (
    <FooterContainer>
      <MainContent>
        <BrandSide>
          <Logo
            src="/images/logos/full-logo.webp"
            alt="Community of Guardians"
          />
          <BrandDescription>
            A global movement uniting scientists, educators, and changemakers to
            translate knowledge into collective action through the UN
            Sustainable Development Goals.
          </BrandDescription>
        </BrandSide>

        <LinksContainer>
          <LinksColumn>
            <ColumnTitle>Explore</ColumnTitle>
            <FooterLink as={NavLink} to="/about">
              About
            </FooterLink>
            <FooterLink as={NavLink} to="/superheroes">
              Superheroes
            </FooterLink>
            <FooterLink as={NavLink} to="/quiz">
              SDG Quiz
            </FooterLink>
          </LinksColumn>

          <LinksColumn>
            <ColumnTitle>Connect</ColumnTitle>
            <FooterLink as={NavLink} to="/contact">
              Contact us
            </FooterLink>
            <FooterLink
              href={EXTERNAL_LINK.donate}
              target="_blank"
              rel="noopener noreferrer"
            >
              Donation
            </FooterLink>
          </LinksColumn>

          <LinksColumn>
            <ColumnTitle>Follow us</ColumnTitle>
            <SocialIcons>
              <SocialIcon
                href={EXTERNAL_LINK.cogFacebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebook size={24} />
              </SocialIcon>
              <SocialIcon
                href={EXTERNAL_LINK.cogInstagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram size={24} />
              </SocialIcon>
              <SocialIcon
                href={EXTERNAL_LINK.cogYoutube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Youtube"
              >
                <FaYoutube size={24} />
              </SocialIcon>
              <SocialIcon
                href={EXTERNAL_LINK.cogLinkein}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <FaLinkedin size={24} />
              </SocialIcon>
            </SocialIcons>
          </LinksColumn>
        </LinksContainer>
      </MainContent>

      <Divider />

      <BottomBar>
        <CopyrightText>
          © {new Date().getFullYear()} Community of Guardians. Force for
          Good™. All rights reserved.
        </CopyrightText>
        <LegalLinks>
          <FooterLink as={NavLink} to="/privacy">
            Privacy Policy
          </FooterLink>
          <FooterLink as={NavLink} to="/terms">
            Term & Condition
          </FooterLink>
        </LegalLinks>
      </BottomBar>
    </FooterContainer>
  )
}

const FooterContainer = styled.footer`
  width: 100%;
  background: #ffffff;
  padding: 80px 40px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 60px 24px 24px;
  }
`

const MainContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  gap: 32px;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 48px;
  }
`

const BrandSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 48px;
  max-width: 443px;
  width: 100%;
`

const Logo = styled.img.attrs({ loading: 'lazy' })`
  width: 230px;
  height: auto;
  object-fit: contain;
`

const BrandDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #5e5e5e;
  margin: 0;
`

const LinksContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 80px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 32px;
  }
`

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  min-width: 100px;
`

const ColumnTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: #2a2a2a;
  margin: 0;
`

const FooterLink = styled.a`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #2a2a2a;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #0e6409;
  }
`

const SocialIcons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`

const SocialIcon = styled.a`
  color: #2a2a2a;
  transition: color 0.2s ease;

  &:hover {
    color: #bbe148;
  }
`

const Divider = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 16px auto 0;
  border: 1px solid #c4c4c4;
`

const BottomBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const CopyrightText = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #5e5e5e;
  margin: 0;
`

const LegalLinks = styled.div`
  display: flex;
  flex-direction: row;
  gap: 21px;

  ${FooterLink} {
    color: #5e5e5e;

    &:hover {
      color: #2a2a2a;
    }
  }
`

export default Footer
